import { formatEvo, type Sample } from '../../model';
import { ChartFrame, type ChartLine } from '../ChartFrame';
import { useLab } from '../LabContext';
import { EvolutionParams } from '../ScenarioPanel';
import { PageLink } from '../router';

const LINES: ChartLine[] = [
  { key: 'evo', label: 'Evolution', color: '#7fd1ff', accessor: (s: Sample) => s.evo },
];

/** Evolution is monotonic in mutagen, so the extent is just the endpoints. */
const yDomain = (samples: Sample[]): [number, number] =>
  samples.length ? [samples[0].evo, samples[samples.length - 1].evo] : [0, 1];

/** The live loop, and the closed form that replaces it. */
function EvolutionInfo() {
  const { ruleset, comparing } = useLab();

  if (comparing) {
    return (
      <>
        <p>
          One flask is worth <code>value × difficulty</code> mutagen. What that buys is the whole
          disagreement between the branches:
        </p>
        <p className="formula">
          live: dE/dM = 2 / (u · 10^0.015u) 3v3: E = (M / 276.66) ^ 0.3
        </p>
        <p>
          The lines cross a little under <b>90% evolution</b>. Below it the test branch is the
          cheaper of the two; above it live's rate freezes at a flat 16.5 mutagen per evolution
          point while the test branch keeps charging more, so by 330% live is there for about a
          third of the mutagen.
        </p>
        <p>
          Both are drawn against the same x axis — the same total mutagen fed — so the vertical gap
          at any point is exactly what the same send is worth on each branch.
        </p>
        <p>
          Difficulty rescales flasks into mutagen, never mutagen into evolution: changing it moves
          neither curve, only the flask count needed to walk along them.
        </p>
      </>
    );
  }

  if (!ruleset.hasInstantThreat) {
    return (
      <>
        <p>
          One flask is worth <code>value × difficulty</code> mutagen. On{' '}
          <code>{ruleset.branch}</code> the feeding loop is gone entirely — evolution is a closed
          form in the total mutagen ever fed:
        </p>
        <p className="formula">E = (M / 276.66) ^ 0.3</p>
        <p>
          Which makes evolution genuinely path-independent: the branch reconstructs the food behind
          your current evolution on every feed, so ten small sends and one big send land in exactly
          the same place. On live they differ by a percent or two, because the loop reads the
          post-increment evolution.
        </p>
        <p>
          The shape is different too. Live freezes its rate past 100% at a flat 16.5 mutagen per
          evolution point; this one keeps charging more forever, so the same 330% costs about 3.6×
          what it does on live.
        </p>
        <p>
          Difficulty rescales flasks into mutagen, never mutagen into evolution: changing it moves
          no curve, only the flask count needed to walk along one.
        </p>
      </>
    );
  }

  return (
    <>
      <p>
        One flask is worth <code>value × difficulty</code> mutagen. Evolution buys itself at
      </p>
      <p className="formula">dE/dM = 2 / (u · 10^0.015u), u = 100·min(E,1) + 1</p>
      <p>
        so the return collapses as evolution climbs. Above 100% the rate freezes at a constant 16.5
        mutagen per +1% — the clamp on <code>u</code> is what makes that happen.
      </p>
      <p>
        Difficulty rescales flasks into mutagen, never mutagen into evolution: changing it moves no
        curve, only the flask count needed to walk along one.
      </p>
    </>
  );
}

export function EvolutionChart() {
  const { tuning } = useLab();
  return (
    <ChartFrame
      index={1}
      title="Evolution"
      info={<EvolutionInfo />}
      infoMore={
        <PageLink page="evolution" section="price">
          What a point of evolution costs
        </PageLink>
      }
      lines={LINES}
      yDomain={yDomain}
      formatY={(value) => formatEvo(value, 0)}
      yLabel="evo"
      showTopAxis
      params={tuning ? <EvolutionParams /> : undefined}
    />
  );
}
